import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, XCircle, PlayCircle } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { runMcpDiagnostics } from "@/lib/mcp-diagnostics.functions";

export const Route = createFileRoute("/_authenticated/admin/mcp-diagnostics")({
  head: () => ({
    meta: [
      { title: "Diagnóstico MCP — Panel Mostrando México" },
      { name: "description", content: "Prueba Bókun y las herramientas de búsqueda y detalle de tours." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

type Tour = { slug: string; title: string; location?: string | null; price_from?: number | null; currency?: string | null };

function Page() {
  const runFn = useServerFn(runMcpDiagnostics);
  const m = useMutation({ mutationFn: () => runFn() });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-4 py-8">
        <AdminNav />
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Diagnóstico MCP</h1>
            <p className="text-sm text-muted-foreground">
              Ejecuta las mismas herramientas que usa ChatGPT: Bókun, búsqueda, «Holbox» y detalle.
            </p>
          </div>
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            <PlayCircle className="mr-2 h-4 w-4" />
            {m.isPending ? "Probando…" : "Ejecutar diagnóstico"}
          </Button>
        </div>

        {m.error && <p className="mb-4 text-sm text-destructive">{(m.error as Error).message}</p>}

        <div className="space-y-4">
          {m.data?.steps.map((s) => {
            const d = (s.data ? JSON.parse(s.data) : null) as Record<string, unknown> | null;
            const tours = (d?.tours as Tour[] | undefined) ?? null;
            return (
              <Card key={s.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    {s.ok ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <XCircle className="h-5 w-5 text-destructive" />}
                    {s.label}
                    <span className="ml-auto text-xs font-normal text-muted-foreground">{s.ms} ms</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  {s.error && <p className="text-destructive">{s.error}</p>}
                  {s.id === "bokun" && d && (
                    <p>{String(d.bokun)} productos de Bókun · {String(d.total)} en total en el catálogo</p>
                  )}
                  {tours && (
                    <>
                      <p className="mb-2 text-muted-foreground">
                        {String(d?.count)} mostrados de {String(d?.total_matches)} coincidencias
                      </p>
                      <ul className="divide-y divide-border">
                        {tours.map((t) => (
                          <li key={t.slug} className="py-1.5">
                            <span className="font-medium text-foreground">{t.title}</span>
                            <span className="text-muted-foreground">
                              {" "}· {t.location ?? "—"}
                              {t.price_from != null ? ` · desde ${t.price_from} ${t.currency ?? ""}` : ""}
                            </span>
                            <div className="text-xs text-muted-foreground">{t.slug}</div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {s.id === "detail" && d && (
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">{String(d.title)}</p>
                      <p className="text-muted-foreground">
                        {String(d.location ?? "—")} · {String(d.duration_text ?? d.duration_minutes ?? "—")}
                      </p>
                      <p>
                        Horarios: {((d.start_times as { label: string | null; hour: number; minute: number }[]) ?? [])
                          .map((t) => t.label ?? `${t.hour}:${String(t.minute).padStart(2, "0")}`)
                          .join(", ") || "—"}
                      </p>
                      <p>
                        Categorías: {((d.pricing_categories as { title: string }[]) ?? []).map((p) => p.title).join(", ") || "—"}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
